import { usePostMatchChecksIncomingMutation } from 'API/accounting/AccountingApi';
import { IPotentialMatch, useGetPotentialEscrowFile, useGetPotentialOrderInfo } from './IPotentialMatch';
import React, { useEffect, useState } from 'react';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { ErrorMessageTW } from 'tailwindComponents/errorMessage/ErrorMessageTW';
import { convertToError } from 'API/BaseApi';
import {
  useCreateCheckDepositMutation,
  useDeleteACHDepositMutation,
  useDeleteWireDepositMutation,
} from 'API/order/escrow/EscrowBaseAPI';
import { ICheckForm } from './Forms';
import { SpinnerTW } from 'tailwindComponents/spinnerTW/SpinnerTW';
import { IEscrowExpectedIncomingCheckData } from 'types/accounting/data/IEscrowExpectedIncomingCheckData';
import { ConfirmCheckForm } from './ConfirmCheckForm';
import { PotentialDisplay } from './PotentialDisplay';
import { assertDefined } from 'utilities/Assertions';

export interface IConfirmCheckMatchProps {
  actualId: string;
  accountNumber: string;
  potentialMatch: IPotentialMatch;
  onClose: () => void;
  amountCents: number;
  amountCentsMax: number;
}

export const ConfirmCheckMatch: React.FC<IConfirmCheckMatchProps> = ({
  actualId,
  accountNumber,
  potentialMatch,
  onClose,
  amountCents,
  amountCentsMax,
}) => {
  const order = useGetPotentialOrderInfo(potentialMatch);
  const escrowFile = useGetPotentialEscrowFile(potentialMatch);
  const [doMatch, { error }] = usePostMatchChecksIncomingMutation();
  const [createIncomingCheck, { isLoading: isLoadingCreateIncomingCheck }] = useCreateCheckDepositMutation();
  const [deleteIncomingWire] = useDeleteWireDepositMutation();
  const [deleteIncomingACH] = useDeleteACHDepositMutation();
  const [expectedCheck, setExpectedCheck] = useState<IEscrowExpectedIncomingCheckData | undefined>(
    potentialMatch.expectedCheck
  );
  useEffect(() => {
    setExpectedCheck(potentialMatch.expectedCheck);
  }, [potentialMatch, setExpectedCheck]);

  const onMatch = async () => {
    assertDefined(expectedCheck);

    const result = await doMatch({ actualId, expectedId: expectedCheck.id });

    if (checkForMutationSuccess(result, 'matching check')) {
      if (potentialMatch.expectedWire?.id) {
        await deleteIncomingWire({ id: potentialMatch.expectedWire.id, orderId: order.id });
      }
      if (potentialMatch.expectedACH?.id) {
        await deleteIncomingACH({ id: potentialMatch.expectedACH.id, orderId: order.id });
      }
      onClose();
    }
  };

  const onCreate = async (x: ICheckForm) => {
    if (order?.id && escrowFile?.id) {
      const result = await createIncomingCheck({ ...x, orderId: order.id, escrowFileId: escrowFile.id });

      checkForMutationSuccess(result, 'creating an expected wire');
      if ('data' in result) {
        setExpectedCheck({
          ...result.data,
          escrowFile: { ...escrowFile, order: order } as any,
        });
      }
    }
  };

  return (
    <>
      <ErrorMessageTW error={convertToError(error)} />
      {!expectedCheck && !isLoadingCreateIncomingCheck && (
        <>
          <ConfirmCheckForm
            potentialMatch={potentialMatch}
            createExpected={onCreate}
            amountCents={amountCents}
            amountCentsMax={amountCentsMax}
          />
        </>
      )}
      {isLoadingCreateIncomingCheck && <SpinnerTW />}

      <PotentialDisplay
        potentialMatch={expectedCheck ? { expectedCheck } : potentialMatch}
        accountNumber={accountNumber}
        onMatch={expectedCheck && onMatch}
      />
    </>
  );
};
